// Pure derivations for the client document collection tab. No MUI, no axios,
// no React here so this stays cheap to unit test.

export const STALE_REQUEST_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const REQUEST_STATUS_CHIPS = {
  REVOKED: { label: "Revoked", color: "default" },
  SUBMITTED: { label: "Submitted", color: "success" },
};

const ACTIVE_PROGRESS_CHIPS = {
  READY: { label: "Ready to submit", color: "info" },
  IN_PROGRESS: { label: "In progress", color: "warning" },
  AWAITING: { label: "Awaiting client", color: "warning" },
};

const DOCUMENT_STATUS_CHIPS = {
  IN_PROGRESS: { label: "In progress", color: "warning" },
  COLLECTED: { label: "Collected", color: "success" },
  SUPERSEDED: { label: "Replaced", color: "default" },
  REMOVED: { label: "Removed", color: "default" },
};

const DEFAULT_DOCUMENT_STATUS_CHIP = { label: "Unknown", color: "default" };

const FROM_REVOKED_LINK_CHIP = { label: "From revoked link", color: "default" };

/** An item counts as progress once the client has attached something for it. */
const itemHasProgress = (item) => item?.state === "FULFILLED";

/**
 * Status chip for a document request. ACTIVE requests get one of three
 * sub-states derived from item fulfilment since the backend does not expose
 * an intermediate status of its own.
 */
export const deriveRequestStatus = (request) => {
  const status = request?.status;
  if (status === "REVOKED" || status === "SUBMITTED") {
    return REQUEST_STATUS_CHIPS[status];
  }

  const items = request?.items ?? [];
  const hasItems = items.length > 0;
  const allFulfilled = hasItems && items.every(itemHasProgress);
  const anyFulfilled = items.some(itemHasProgress);

  if (allFulfilled) {
    return ACTIVE_PROGRESS_CHIPS.READY;
  }
  if (anyFulfilled) {
    return ACTIVE_PROGRESS_CHIPS.IN_PROGRESS;
  }
  return ACTIVE_PROGRESS_CHIPS.AWAITING;
};

/**
 * A request is stale when it has sat ACTIVE (nobody has submitted or revoked
 * it) for longer than the staleness window. Submitted/revoked requests are
 * resolved, so their age is irrelevant.
 */
export const isStale = (request, now) => {
  if (request?.status !== "ACTIVE") {
    return false;
  }
  if (!request?.createdAt) {
    return false;
  }
  const created = new Date(request.createdAt);
  if (Number.isNaN(created.getTime())) {
    return false;
  }
  const reference = now instanceof Date ? now : new Date(now);
  const ageMs = reference.getTime() - created.getTime();
  return ageMs > STALE_REQUEST_DAYS * MS_PER_DAY;
};

/**
 * Status chip for a single collected document. `requestStatus` is the parent request's own
 * status, not stored on the document itself: revoking a request never touches the files already
 * uploaded under it, so a file left IN_PROGRESS when its link died would otherwise sit labelled
 * "In progress" forever, indistinguishable from one someone could still finish.
 */
export const documentStatusChip = (document, requestStatus) => {
  const status = document?.status;
  if (status === "IN_PROGRESS" && requestStatus === "REVOKED") {
    return FROM_REVOKED_LINK_CHIP;
  }
  return DOCUMENT_STATUS_CHIPS[status] ?? DEFAULT_DOCUMENT_STATUS_CHIP;
};

const EVENT_LABELS = {
  OTP_VERIFIED: "Client verified their identity",
  UPLOADED: "File uploaded",
  REPLACED: "File replaced",
  REMOVED: "File removed",
  SUBMITTED: "Documents submitted",
  REVOKED: "Request revoked",
  FLAGGED: "Item flagged for reupload",
};

const DEFAULT_EVENT_LABEL = "Activity";

/** A null actorName on the backend means the client acted through the portal. */
const CLIENT_ACTOR = "Client";

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;

/**
 * The backend already splits an item's files into live and history, so this
 * only normalizes the shape and copies the arrays, keeping callers from
 * mutating the query cache in place.
 */
export const liveAndHistory = (item) => ({
  live: [...(item?.documents ?? [])],
  history: [...(item?.history ?? [])],
});

/** Readable label and actor for one timeline entry. */
export const describeEvent = (event) => ({
  label: EVENT_LABELS[event?.eventType] ?? DEFAULT_EVENT_LABEL,
  actor: event?.actorName || CLIENT_ACTOR,
});

export const formatFileSize = (sizeBytes) => {
  if (sizeBytes === null || sizeBytes === undefined || Number.isNaN(Number(sizeBytes))) {
    return "-";
  }
  const bytes = Number(sizeBytes);
  if (bytes >= BYTES_PER_MB) {
    return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
  }
  if (bytes >= BYTES_PER_KB) {
    return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

// Uploads allow doc/docx/xls/xlsx/heic too, but no browser renders those inline,
// so offering preview for them would just open a blank frame.
const PREVIEWABLE_EXTENSIONS = {
  pdf: "pdf",
  jpg: "image",
  jpeg: "image",
  png: "image",
  webp: "image",
};

const PREVIEWABLE_MIME_TYPES = {
  "application/pdf": "pdf",
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
};

const extensionOf = (filename) => {
  const name = typeof filename === "string" ? filename : "";
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
};

/** "pdf" | "image" | "none" - which renderer the preview dialog should use. */
export const previewKind = (document) => {
  const byExtension = PREVIEWABLE_EXTENSIONS[extensionOf(document?.originalFilename)];
  if (byExtension) return byExtension;
  const byMime = PREVIEWABLE_MIME_TYPES[document?.contentType];
  return byMime || "none";
};

export const isPreviewable = (document) => previewKind(document) !== "none";

/**
 * Items that can be sent back for a corrected copy. An item already FLAGGED has an open round
 * against it, so offering it again would mail the client a second link for the same document.
 */
export const reuploadableItems = (request) =>
  (request?.items ?? []).filter((item) => item?.state !== "FLAGGED");

/**
 * Reupload is only meaningful once the client has closed the request: while it is ACTIVE they
 * can still replace a file themselves, and a REVOKED request has no live link to follow up on.
 */
export const canRequestReupload = (request) =>
  request?.status === "SUBMITTED" && reuploadableItems(request).length > 0;

const MODULE_LABELS = {
  VISA: "Visa",
  INSURANCE: "Insurance",
  RPL_CLIENT: "RPL",
  SKILL_ASSESSMENT: "Skill Assessment",
  ADMISSION: "Admission",
};

// Route segments under /admin, from AdminRoutes.js. A module missing here simply gets no
// deep link rather than a broken one.
const MODULE_ROUTE_SEGMENTS = {
  VISA: "visa",
  INSURANCE: "insurance",
  RPL_CLIENT: "rpl-client",
  SKILL_ASSESSMENT: "skill-assessment",
  ADMISSION: "admission",
};

/** Falls back to the raw enum so a module added on the backend still renders something. */
export const moduleLabel = (moduleType) => MODULE_LABELS[moduleType] ?? (moduleType || "");

export const moduleEntryPath = (moduleType, entryId) => {
  const segment = MODULE_ROUTE_SEGMENTS[moduleType];
  if (!segment || entryId === null || entryId === undefined) {
    return null;
  }
  return `/admin/${segment}/edit/${entryId}`;
};

const matchesText = (value, needle) =>
  typeof value === "string" && value.toLowerCase().includes(needle);

/**
 * Narrows the mirrored documents by module and free text. Matching the entry label keeps that
 * entry whole, since someone searching for a visa subclass wants everything filed under it, not
 * only the files whose own name happens to repeat the label.
 */
export const filterModuleGroups = (groups, { search, moduleType } = {}) => {
  const needle = (search || "").trim().toLowerCase();
  return (groups ?? [])
    .filter((group) => !moduleType || group?.moduleType === moduleType)
    .map((group) => {
      const entries = (group?.entries ?? [])
        .map((entry) => {
          if (!needle || matchesText(entry?.entryLabel, needle)) {
            return entry;
          }
          const documents = (entry?.documents ?? []).filter((document) =>
            matchesText(document?.name, needle)
          );
          return { ...entry, documents };
        })
        .filter((entry) => (entry?.documents ?? []).length > 0);
      return { ...group, entries };
    })
    .filter((group) => group.entries.length > 0);
};

/**
 * Narrows the collected requests by free text over the request title, its round label and the
 * labels of the documents it asked for.
 */
export const filterRequests = (requests, search) => {
  const needle = (search || "").trim().toLowerCase();
  if (!needle) {
    return requests ?? [];
  }
  return (requests ?? []).filter(
    (request) =>
      matchesText(request?.title, needle) ||
      matchesText(request?.roundLabel, needle) ||
      (request?.items ?? []).some((item) => matchesText(item?.label, needle))
  );
};

/** Collects the flat entry list into per-module buckets for the show-in-module picker. */
export const groupEntriesByModule = (entries) => {
  const byModule = new Map();
  (entries ?? []).forEach((entry) => {
    if (!entry?.moduleType) {
      return;
    }
    if (!byModule.has(entry.moduleType)) {
      byModule.set(entry.moduleType, []);
    }
    byModule.get(entry.moduleType).push(entry);
  });
  return [...byModule.entries()].map(([moduleType, moduleEntries]) => ({
    moduleType,
    entries: moduleEntries,
  }));
};

/** Hands a fetched blob to the browser as a download, then releases the object URL. */
export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = filename || "document";
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
